import { RefreshCw, Upload, X } from "lucide-react";

const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Modal for uploading a new firmware binary.
 *
 * Props:
 *   isOpen       - Boolean controlling visibility
 *   onClose      - Callback to close the modal
 *   form         - Object: { version, description, is_stable, is_latest }
 *   onFormChange - (updatedForm) => void
 *   file         - Selected File object (or null)
 *   onFileChange - (File | null) => void
 *   onUpload     - async () => void
 *   uploading    - Boolean; shows spinner when true
 */
const FirmwareUploadModal = ({
  isOpen,
  onClose,
  form,
  onFormChange,
  file,
  onFileChange,
  onUpload,
  uploading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
      >
        <div className="flex items-center justify-between border-b p-6">
          <h3
            id="upload-modal-title"
            className="text-xl font-semibold text-gray-900 flex items-center gap-2"
          >
            <Upload className="w-6 h-6 text-blue-600" />
            Upload Firmware
          </h3>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Firmware File (.bin)
            </label>
            <input
              type="file"
              accept=".bin"
              onChange={(e) => onFileChange(e.target.files[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {file && (
              <p className="text-sm text-gray-600 mt-1">
                Selected: {file.name} ({formatFileSize(file.size)})
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Version *
            </label>
            <input
              type="text"
              value={form.version}
              onChange={(e) => onFormChange({ ...form, version: e.target.value })}
              placeholder="e.g., 2.1.0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description / Release Notes
            </label>
            <textarea
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              rows={3}
              placeholder="What's new in this version..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_stable}
                onChange={(e) => onFormChange({ ...form, is_stable: e.target.checked })}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Mark as Stable/Production</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_latest}
                onChange={(e) => onFormChange({ ...form, is_latest: e.target.checked })}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Mark as Latest</span>
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> MD5 and SHA256 checksums will be calculated automatically.
              The firmware will be available for deployment immediately after upload.
            </p>
          </div>
        </div>

        <div className="flex gap-3 border-t p-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onUpload}
            disabled={uploading || !file || !form.version}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Firmware
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmwareUploadModal;
